import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Archive,
  ChatBubbleOutline,
  Close,
  Delete,
  Download,
  InfoOutlined,
  MoreVert,
  NotificationsOffOutlined,
  RestoreFromTrash,
  Search,
  Tune,
  Unarchive,
  UploadFile,
  Visibility,
} from "@mui/icons-material";
import Layout from "components/Layout";
import AppDialog from "components/common/AppDialog";
import {
  theme as baseTheme,
  CALIBRATE_COLORS,
  CALIBRATE_FONT_FAMILY_ACCENT,
  CALIBRATE_RADIUS_MD,
} from "components/hoc/Theme";
import { SHELL_MAIN_CHROME_PADDING_PX } from "config";
import { useAppDispatch, useAppSelector } from "features/store";
import { pushNotification } from "features/notificationSlice";
import { useFilePreview } from "components/preview";
import { usePermissions } from "hooks/usePermissions";
import { useViewAs } from "hooks/useViewAs";
import { ACTIONS } from "utils/access";
import PaginationControls from "components/fileStorage/PaginationControls";
import SecureFileUploadModal from "components/secureUpload/SecureFileUploadModal";
import {
  SecureFile,
  SecureFileDirection,
  SecureFileDisplayStatus,
  SecureFileTab,
  useArchiveSecureFileMutation,
  useUnarchiveSecureFileMutation,
  useDeleteSecureFileMutation,
  useGetSecureFileAccessUrlMutation,
  useGetSecureFilesQuery,
  useMarkSecureFilesSeenMutation,
  useRestoreSecureFileMutation,
} from "features/secureUploads/secureUploads";

const PAGE_SIZE_DEFAULT = 25;

const UPLOAD_BLUE = "#2f6d9e";

const secureTheme = createTheme(baseTheme, {
  components: {
    MuiTableCell: {
      styleOverrides: {
        head: {
          textTransform: "uppercase" as const,
          fontSize: 11,
          letterSpacing: "0.07em",
          fontWeight: 700,
        },
      },
    },
  },
});

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

const isNearDeletion = (deletesOn: string | null): boolean => {
  if (!deletesOn) return false;
  const days = (new Date(deletesOn).getTime() - Date.now()) / 86400000;
  return days >= 0 && days <= 7;
};

const STATUS_META: Record<
  SecureFileDisplayStatus,
  { label: string; color: "info" | "success" | "default" }
> = {
  new: { label: "NEW", color: "info" },
  downloaded: { label: "DOWNLOADED", color: "success" },
  sent: { label: "SENT", color: "default" },
  received: { label: "RECEIVED", color: "success" },
  uploaded: { label: "UPLOADED", color: "default" },
};

const StatusChip = ({ status }: { status: SecureFileDisplayStatus }) => {
  const meta = STATUS_META[status] ?? STATUS_META.new;
  return (
    <Chip
      label={meta.label}
      size="small"
      color={meta.color}
      variant="outlined"
      sx={{ fontWeight: 600, letterSpacing: 0.4 }}
    />
  );
};

const SilentMarker = () => (
  <Tooltip title="Sent without notification">
    <NotificationsOffOutlined
      aria-label="Sent without notification"
      sx={{ fontSize: 16, color: "text.disabled" }}
    />
  </Tooltip>
);

export default function SecureFileUploadPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const { openInlinePreview } = useFilePreview();

  const { isClientView, profile } = useViewAs();
  const { sees } = usePermissions();
  // Staff pick the client they share with and may send silently; a client user
  // sends to their own team.
  const isInternalUploader = profile?.user_type === "internal";
  const seesDeletedFiles = sees(ACTIONS.SECURE_EXCHANGE_DELETED);

  const [tab, setTab] = useState<SecureFileTab>("shared");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    SecureFileDisplayStatus | "all"
  >("all");
  const [directionFilter, setDirectionFilter] = useState<
    SecureFileDirection | "all"
  >("all");
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE_DEFAULT);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const handleSort = (field: string) => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortField(undefined);
        setSortOrder("asc");
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuItem, setMenuItem] = useState<SecureFile | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [detailItem, setDetailItem] = useState<SecureFile | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: "archive" | "delete" | "restore";
    item: SecureFile;
  } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  useEffect(() => {
    if (!seesDeletedFiles && tab === "deleted") setTab("shared");
  }, [seesDeletedFiles, tab]);

  const { data, isLoading } = useGetSecureFilesQuery(
    {
      tab,
      search: search.trim() || undefined,
      display_status: statusFilter === "all" ? undefined : statusFilter,
      direction: directionFilter === "all" ? undefined : directionFilter,
      sort_by: sortField,
      sort_order: sortField ? sortOrder : undefined,
      page,
      page_size: limit,
    },
    {
      pollingInterval: 20000,
    },
  );

  const [getAccessUrl] = useGetSecureFileAccessUrlMutation();
  const [archiveFile] = useArchiveSecureFileMutation();
  const [unarchiveFile] = useUnarchiveSecureFileMutation();
  const [deleteFile] = useDeleteSecureFileMutation();
  const [restoreFile] = useRestoreSecureFileMutation();
  const [markSeen] = useMarkSecureFilesSeenMutation();

  // Opening the page clears the sidebar badge; NEW chips persist until download.
  useEffect(() => {
    if (!isClientView) markSeen();
  }, []);

  const files = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));

  const openMenu = (e: React.MouseEvent<HTMLElement>, item: SecureFile) => {
    setMenuAnchor(e.currentTarget);
    setMenuItem(item);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  const handleDownload = async (item: SecureFile) => {
    closeMenu();
    setBusyId(item.id);
    try {
      const { url } = await getAccessUrl({ id: item.id }).unwrap();
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      dispatch(
        pushNotification({ message: "Failed to download file.", severity: "error" }),
      );
    } finally {
      setBusyId(null);
    }
  };

  const handlePreview = async (item: SecureFile) => {
    closeMenu();
    setBusyId(item.id);
    try {
      const { url, file_name } = await getAccessUrl({
        id: item.id,
        mode: "preview",
      }).unwrap();
      const res = await fetch(url);
      const blob = await res.blob();
      openInlinePreview(file_name, blob, { size: item.size });
    } catch {
      dispatch(
        pushNotification({ message: "Failed to preview file.", severity: "error" }),
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleArchive = async (item: SecureFile) => {
    closeMenu();
    try {
      await archiveFile(item.id).unwrap();
      dispatch(pushNotification({ message: "File archived.", severity: "success" }));
    } catch {
      dispatch(
        pushNotification({ message: "Failed to archive file.", severity: "error" }),
      );
    }
  };

  const handleUnarchive = async (item: SecureFile) => {
    closeMenu();
    try {
      await unarchiveFile(item.id).unwrap();
      dispatch(
        pushNotification({ message: "File unarchived.", severity: "success" }),
      );
    } catch {
      dispatch(
        pushNotification({ message: "Failed to unarchive file.", severity: "error" }),
      );
    }
  };

  const handleDelete = async (item: SecureFile) => {
    closeMenu();
    try {
      await deleteFile(item.id).unwrap();
      dispatch(pushNotification({ message: "File deleted.", severity: "success" }));
    } catch {
      dispatch(
        pushNotification({ message: "Failed to delete file.", severity: "error" }),
      );
    }
  };

  const handleRestore = async (item: SecureFile) => {
    closeMenu();
    try {
      await restoreFile(item.id).unwrap();
      dispatch(
        pushNotification({
          message: "File restored to Shared Files.",
          severity: "success",
        }),
      );
    } catch {
      dispatch(
        pushNotification({ message: "Failed to restore file.", severity: "error" }),
      );
    }
  };

  // Archive, Delete and Restore are gated behind a confirmation dialog.
  const runConfirm = async () => {
    if (!confirmAction) return;
    const { type, item } = confirmAction;
    setConfirmBusy(true);
    if (type === "archive") {
      await handleArchive(item);
    } else if (type === "restore") {
      await handleRestore(item);
    } else {
      await handleDelete(item);
    }
    setConfirmBusy(false);
    setConfirmAction(null);
  };

  const isArchivedTab = tab === "archived";
  const isDeletedTab = tab === "deleted";
  const columns: { label: string; field?: string }[] = isDeletedTab
    ? [
        { label: "File Name", field: "file_name" },
        { label: "Client", field: "client" },
        { label: "Uploaded By", field: "uploaded_by" },
        { label: "Deleted On", field: "deleted_at" },
        { label: "Purges On", field: "purges_on" },
        { label: "Deleted By" },
        { label: "Reason" },
        { label: "" },
      ]
    : isArchivedTab
    ? [
        { label: "File Name", field: "file_name" },
        { label: "Client", field: "client" },
        { label: "Uploaded By", field: "uploaded_by" },
        { label: "Uploaded For", field: "uploaded_for" },
        { label: "Archived On", field: "archived_at" },
        { label: "Deletes On", field: "deletes_on" },
        { label: "Status", field: "status" },
        { label: "" },
      ]
    : [
        { label: "File Name", field: "file_name" },
        { label: "Client", field: "client" },
        { label: "Uploaded By", field: "uploaded_by" },
        { label: "Uploaded For", field: "uploaded_for" },
        { label: "Date Shared", field: "created_at" },
        { label: "Status", field: "status" },
        { label: "" },
      ];

  const pagination =
    totalItems > 0 ? (
      <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
        <PaginationControls
          currentLimit={limit}
          onLimitChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
          totalItems={totalItems}
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          showLimitSelector
          showPageInfo
          disabled={isLoading}
          size="small"
          hasNextPage={page < totalPages}
          borderStyle={{ borderTop: "none" }}
        />
      </Box>
    ) : null;

  const SKELETON_ROWS = 8;

  const mobileList = isLoading ? (
    <Stack divider={<Divider />}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Box key={i} sx={{ p: 2 }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="45%" />
          <Skeleton variant="text" width="50%" />
        </Box>
      ))}
    </Stack>
  ) : files.length === 0 ? (
    <Typography color="text.secondary" sx={{ textAlign: "center", py: 6 }}>
      No files to display.
    </Typography>
  ) : (
    <Stack divider={<Divider />}>
      {files.map((f) => (
        <Box key={f.id} sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontWeight: 700, wordBreak: "break-word" }}>
              {f.file_name}
            </Typography>
            <IconButton size="small" onClick={(e) => openMenu(e, f)}>
              {busyId === f.id ? <CircularProgress size={18} /> : <MoreVert />}
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {f.client_name} ·{" "}
            {isDeletedTab
              ? `Deleted ${formatDate(f.deleted_at)}`
              : isArchivedTab
              ? `Archived ${formatDate(f.archived_at)}`
              : formatDate(f.created_at)}
          </Typography>
          {isArchivedTab && (
            <Typography
              variant="body2"
              sx={{
                color: isNearDeletion(f.deletes_on)
                  ? "calibrate.accentOrange"
                  : "text.secondary",
                fontWeight: isNearDeletion(f.deletes_on) ? 600 : 400,
              }}
            >
              Deletes on {formatDate(f.deletes_on)}
            </Typography>
          )}
          {isDeletedTab && (
            <Typography variant="body2" color="text.secondary">
              Purges on {formatDate(f.purges_on)}
              {f.deleted_by_name ? ` · Deleted by ${f.deleted_by_name}` : ""}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            From {f.uploaded_by_name} → {f.uploaded_for_name}
          </Typography>
          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
            {isDeletedTab ? (
              <Chip
                label={f.delete_reason_display}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, letterSpacing: 0.4 }}
              />
            ) : (
              <StatusChip status={f.display_status} />
            )}
            {f.is_silent && <SilentMarker />}
            {f.message && (
              <Button
                size="small"
                startIcon={<ChatBubbleOutline sx={{ fontSize: 16 }} />}
                onClick={() => setDetailItem(f)}
                sx={{ textTransform: "none" }}
              >
                Message
              </Button>
            )}
          </Box>
        </Box>
      ))}
    </Stack>
  );

  const desktopTable = (
    <Table stickyHeader>
      <TableHead>
        <TableRow sx={{ "& th": { bgcolor: "grey.50" } }}>
          {columns.map((c, i) => (
            <TableCell
              key={i}
              align={i === columns.length - 1 ? "right" : "left"}
              sortDirection={
                c.field && sortField === c.field ? sortOrder : false
              }
            >
              {c.field ? (
                <TableSortLabel
                  active={sortField === c.field}
                  direction={sortField === c.field ? sortOrder : "asc"}
                  onClick={() => handleSort(c.field as string)}
                >
                  {c.label}
                </TableSortLabel>
              ) : (
                c.label
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <TableRow key={`sk-${i}`}>
              {columns.map((_c, j) => (
                <TableCell
                  key={j}
                  align={j === columns.length - 1 ? "right" : "left"}
                >
                  {j === columns.length - 1 ? null : (
                    <Skeleton variant="text" width={j === 0 ? 160 : "80%"} />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : files.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              sx={{ textAlign: "center", py: 6, color: "text.secondary" }}
            >
              No files to display.
            </TableCell>
          </TableRow>
        ) : (
          files.map((f) => (
          <TableRow key={f.id} hover>
            <TableCell sx={{ wordBreak: "break-word", maxWidth: 280 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {f.file_name}
                {f.message && (
                  <IconButton
                    size="small"
                    aria-label="View message"
                    onClick={() => setDetailItem(f)}
                    sx={{ color: "info.main" }}
                  >
                    <ChatBubbleOutline sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            </TableCell>
            <TableCell>{f.client_name}</TableCell>
            <TableCell>{f.uploaded_by_name}</TableCell>
            {isDeletedTab ? (
              <>
                <TableCell>{formatDate(f.deleted_at)}</TableCell>
                <TableCell>{formatDate(f.purges_on)}</TableCell>
                <TableCell>{f.deleted_by_name || "—"}</TableCell>
                <TableCell>
                  <Chip
                    label={f.delete_reason_display}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600, letterSpacing: 0.4 }}
                  />
                </TableCell>
              </>
            ) : (
              <>
                <TableCell>{f.uploaded_for_name}</TableCell>
                {isArchivedTab ? (
                  <>
                    <TableCell>{formatDate(f.archived_at)}</TableCell>
                    <TableCell
                      sx={
                        isNearDeletion(f.deletes_on)
                          ? { color: "calibrate.accentOrange", fontWeight: 600 }
                          : undefined
                      }
                    >
                      {formatDate(f.deletes_on)}
                    </TableCell>
                  </>
                ) : (
                  <TableCell>{formatDate(f.created_at)}</TableCell>
                )}
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <StatusChip status={f.display_status} />
                    {f.is_silent && <SilentMarker />}
                  </Box>
                </TableCell>
              </>
            )}
            <TableCell align="right">
              <IconButton size="small" onClick={(e) => openMenu(e, f)}>
                {busyId === f.id ? <CircularProgress size={18} /> : <MoreVert />}
              </IconButton>
            </TableCell>
          </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  const tableBody = isMobile ? mobileList : desktopTable;

  const searchField = (
    <TextField
      size="small"
      placeholder="Search clients or files…"
      value={search}
      onChange={(e) => {
        setSearch(e.target.value);
        setPage(1);
      }}
      sx={{ width: "100%" }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );

  const renderShowFilter = (fullWidth: boolean) => (
    <FormControl
      size="small"
      fullWidth={fullWidth}
      sx={fullWidth ? undefined : { minWidth: 150 }}
    >
      <InputLabel id="secure-direction-filter">Show</InputLabel>
      <Select
        labelId="secure-direction-filter"
        label="Show"
        value={directionFilter}
        onChange={(e) => {
          setDirectionFilter(e.target.value as SecureFileDirection | "all");
          setPage(1);
        }}
      >
        <MenuItem value="all">All files</MenuItem>
        <MenuItem value="received">Received by me</MenuItem>
        <MenuItem value="sent">Sent by me</MenuItem>
      </Select>
    </FormControl>
  );

  const renderStatusFilter = (fullWidth: boolean) => (
    <FormControl
      size="small"
      fullWidth={fullWidth}
      sx={fullWidth ? undefined : { minWidth: 150 }}
    >
      <InputLabel id="secure-status-filter">Status</InputLabel>
      <Select
        labelId="secure-status-filter"
        label="Status"
        value={statusFilter}
        onChange={(e) => {
          setStatusFilter(e.target.value as SecureFileDisplayStatus | "all");
          setPage(1);
        }}
      >
        <MenuItem value="all">All statuses</MenuItem>
        <MenuItem value="new">New</MenuItem>
        <MenuItem value="downloaded">Downloaded</MenuItem>
        <MenuItem value="uploaded">Uploaded</MenuItem>
        <MenuItem value="sent">Sent</MenuItem>
        <MenuItem value="received">Received</MenuItem>
      </Select>
    </FormControl>
  );

  const renderUploadButton = (fullWidth: boolean) => (
    <Tooltip title={isClientView ? "Read-only in client view" : ""}>
      <span style={fullWidth ? { display: "block" } : undefined}>
        <Button
          fullWidth={fullWidth}
          variant="outlined"
          startIcon={<UploadFile />}
          onClick={() => {
            setFilterDrawerOpen(false);
            setUploadOpen(true);
          }}
          disabled={isClientView}
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: UPLOAD_BLUE,
            borderColor: UPLOAD_BLUE,
            "&:hover": {
              borderColor: UPLOAD_BLUE,
              bgcolor: "rgba(47,109,158,0.06)",
            },
          }}
        >
          Upload New File
        </Button>
      </span>
    </Tooltip>
  );

  const chromePad = `${SHELL_MAIN_CHROME_PADDING_PX}px`;

  return (
    <Layout title="Secure Exchange" content="Secure Exchange" noScroll>
      <ThemeProvider theme={secureTheme}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            bgcolor: CALIBRATE_COLORS.chromeBackground,
            p: chromePad,
            boxSizing: "border-box",
            minHeight: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 700,
              mb: 0.25,
              color: "text.primary",
              fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
              flexShrink: 0,
            }}
          >
            Secure Exchange
          </Typography>
          <Typography
            sx={{ color: "text.secondary", mb: 1.5, flexShrink: 0 }}
            variant="body2"
          >
            Share files securely with your CalibrateHCM team.
          </Typography>

          <Box
            sx={(t) => ({
              bgcolor: "background.paper",
              borderRadius: `${CALIBRATE_RADIUS_MD}px`,
              boxShadow: t.shadows[2],
              width: "100%",
              flex: 1,
              minHeight: 0,
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              overflow: "hidden",
            })}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                px: { xs: 2, sm: 3 },
                pt: 2,
                pb: 2,
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  mb: 1.5,
                  flexShrink: 0,
                }}
              >
                <Tabs
                  value={tab}
                  onChange={(_e, v) => {
                    setTab(v);
                    setPage(1);
                    setSortField(undefined);
                    setSortOrder("asc");
                  }}
                  variant={isMobile ? "scrollable" : "standard"}
                  allowScrollButtonsMobile
                  sx={{
                    "& .MuiTab-root": {
                      textTransform: "uppercase",
                      fontWeight: 700,
                      minHeight: 44,
                      fontSize: 12,
                      letterSpacing: "0.06em",
                    },
                    "& .MuiTab-root.Mui-selected": {
                      color: "calibrate.accentOrange",
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "calibrate.accentOrange",
                    },
                  }}
                >
                  <Tab value="shared" label="Shared Files" />
                  <Tab value="archived" label="Archived Files" />
                  {seesDeletedFiles && (
                    <Tab value="deleted" label="Deleted Files" />
                  )}
                </Tabs>
              </Box>

              {tab === "archived" && (
                <Box
                  sx={{
                    mb: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1,
                    bgcolor: "rgba(2,136,209,0.08)",
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="body2" color="info.main">
                    Archived files are automatically deleted 30 days after
                    archiving.
                  </Typography>
                </Box>
              )}

              {tab === "deleted" && (
                <Box
                  sx={{
                    mb: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1,
                    bgcolor: "rgba(2,136,209,0.08)",
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="body2" color="info.main">
                    Deleted files are permanently removed 30 days after
                    deletion. Only super admins can view and restore them.
                  </Typography>
                </Box>
              )}

              {isMobile ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>{searchField}</Box>
                  <IconButton
                    color="primary"
                    aria-label="Open filters and actions"
                    onClick={() => setFilterDrawerOpen(true)}
                    sx={{
                      flexShrink: 0,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Tune />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    alignItems: "center",
                    mb: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ width: 320, maxWidth: "100%" }}>
                    {searchField}
                  </Box>
                  {renderShowFilter(false)}
                  {!isDeletedTab && renderStatusFilter(false)}
                  {tab === "shared" && (
                    <Box sx={{ ml: "auto" }}>{renderUploadButton(false)}</Box>
                  )}
                </Box>
              )}

              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                }}
              >
                <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                  {tableBody}
                </TableContainer>
                {pagination}
              </Box>
            </Box>
          </Box>
        </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            const it = menuItem;
            closeMenu();
            setDetailItem(it);
          }}
        >
          <ListItemIcon>
            <InfoOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>View details</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => menuItem && handlePreview(menuItem)}
        >
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Preview</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => menuItem && handleDownload(menuItem)}
        >
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        {menuItem?.can_restore && (
          <MenuItem
            disabled={isClientView}
            onClick={() => {
              if (!menuItem) return;
              setConfirmAction({ type: "restore", item: menuItem });
              closeMenu();
            }}
          >
            <ListItemIcon>
              <RestoreFromTrash fontSize="small" />
            </ListItemIcon>
            <ListItemText>Restore</ListItemText>
          </MenuItem>
        )}
        {menuItem &&
          !menuItem.is_deleted &&
          (menuItem.is_archived ? (
            <MenuItem
              disabled={isClientView}
              onClick={() => menuItem && handleUnarchive(menuItem)}
            >
              <ListItemIcon>
                <Unarchive fontSize="small" />
              </ListItemIcon>
              <ListItemText>Unarchive</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem
              disabled={isClientView}
              onClick={() => {
                if (!menuItem) return;
                setConfirmAction({ type: "archive", item: menuItem });
                closeMenu();
              }}
            >
              <ListItemIcon>
                <Archive fontSize="small" />
              </ListItemIcon>
              <ListItemText>Archive</ListItemText>
            </MenuItem>
          ))}
        {menuItem?.is_archived && menuItem?.can_delete && (
          <MenuItem
            disabled={isClientView}
            onClick={() => {
              if (!menuItem) return;
              setConfirmAction({ type: "delete", item: menuItem });
              closeMenu();
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <SecureFileUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        isInternalUploader={isInternalUploader}
      />

      <AppDialog
        open={Boolean(detailItem)}
        onClose={() => setDetailItem(null)}
        title="File details"
        maxWidth="sm"
        showCloseIcon
      >
        {detailItem && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <DetailRow label="File" value={detailItem.file_name} />
            <DetailRow label="Client" value={detailItem.client_name ?? "—"} />
            <DetailRow label="Uploaded by" value={detailItem.uploaded_by_name} />
            <DetailRow label="Uploaded for" value={detailItem.uploaded_for_name} />
            <DetailRow label="Date shared" value={formatDate(detailItem.created_at)} />
            {detailItem.is_silent && (
              <DetailRow label="Notification" value="Sent without notification" />
            )}
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              MESSAGE TO RECIPIENT
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                color: detailItem.message ? "text.primary" : "text.secondary",
                fontStyle: detailItem.message ? "normal" : "italic",
              }}
            >
              {detailItem.message || "No message was included."}
            </Typography>
          </Box>
        )}
      </AppDialog>

      <AppDialog
        open={Boolean(confirmAction)}
        onClose={() => {
          if (!confirmBusy) setConfirmAction(null);
        }}
        title={
          confirmAction?.type === "delete"
            ? "Delete file"
            : confirmAction?.type === "restore"
            ? "Restore file"
            : "Archive file"
        }
        maxWidth="xs"
        actions={
          <>
            <Button
              onClick={() => setConfirmAction(null)}
              disabled={confirmBusy}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              onClick={runConfirm}
              disabled={confirmBusy}
              variant="contained"
              color={confirmAction?.type === "delete" ? "error" : "primary"}
              startIcon={
                confirmBusy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {confirmAction?.type === "delete"
                ? "Delete"
                : confirmAction?.type === "restore"
                ? "Restore"
                : "Archive"}
            </Button>
          </>
        }
      >
        {confirmAction && (
          <Typography variant="body2">
            {confirmAction.type === "delete" ? (
              <>
                Delete <b>{confirmAction.item.file_name}</b>? It will no longer
                be visible in Secure Exchange.
              </>
            ) : confirmAction.type === "restore" ? (
              <>
                Restore <b>{confirmAction.item.file_name}</b>? It will return
                to Shared Files.
              </>
            ) : (
              <>
                Archive <b>{confirmAction.item.file_name}</b>? It will move to
                Archived and be automatically deleted 30 days from now. You can
                unarchive it before then.
              </>
            )}
          </Typography>
        )}
      </AppDialog>

      <Drawer
        anchor="bottom"
        open={isMobile && filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            maxHeight: "80vh",
            p: 2,
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6" fontWeight={700}>
            Filters
          </Typography>
          <IconButton
            size="small"
            onClick={() => setFilterDrawerOpen(false)}
            sx={{ borderRadius: 1 }}
          >
            <Close />
          </IconButton>
        </Box>
        <Box display="flex" flexDirection="column" gap={2}>
          {renderShowFilter(true)}
          {!isDeletedTab && renderStatusFilter(true)}
          {tab === "shared" && renderUploadButton(true)}
        </Box>
      </Drawer>
      </ThemeProvider>
    </Layout>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 110, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  );
}
